import { useEffect, useState } from 'react'

export interface VisualViewportState {
  top: number
  left: number
  height: number
  width: number
}

const DEFAULT: VisualViewportState = {
  top: 0,
  left: 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 0,
  width: typeof window !== 'undefined' ? window.innerWidth : 0,
}

/** Tracks the visible viewport — shrinks when the mobile software keyboard opens. */
export function useVisualViewport(enabled: boolean): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(DEFAULT)

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const vv = window.visualViewport
    const update = () => {
      if (vv) {
        setState({
          top: vv.offsetTop,
          left: vv.offsetLeft,
          height: vv.height,
          width: vv.width,
        })
      } else {
        setState({
          top: 0,
          left: 0,
          height: window.innerHeight,
          width: window.innerWidth,
        })
      }
    }

    update()
    vv?.addEventListener('resize', update)
    vv?.addEventListener('scroll', update)
    window.addEventListener('resize', update)

    return () => {
      vv?.removeEventListener('resize', update)
      vv?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [enabled])

  return state
}

export function useIsMobileLayout(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  )

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)')
    const onChange = () => setIsMobile(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return isMobile
}
